
import { useState, useEffect, useCallback, useRef } from 'react';
import { AdminState, AdminAction } from '@/types/adminTypes';
import { useAdminStateSync } from './useAdminStateSync';
import { useAdminActions } from './useAdminActions';
import { useAdminRealtime } from './useAdminRealtime';
import { removeActiveAdmin as removeAdminService } from '@/services/adminActionService';
import { useToast } from '@/hooks/use-toast';

// Import our smaller hooks
import { useAdminActivityState } from './admin/useAdminActivityState';
import { useAdminActivityRefresh } from './admin/useAdminActivityRefresh';
import { useAdminActionRecording } from './admin/useAdminActionRecording';
import { useAdminActiveUser } from './admin/useAdminActiveUser';

/**
 * Main hook for admin activity tracking with no automatic polling
 */
export const useAdminActivity = (pollingInterval = 0) => { // Set to 0 to disable polling
  const { toast } = useToast();
  
  // Use the activity state hook
  const {
    isLoading,
    setIsLoading,
    isActionThrottled,
    setIsActionThrottled
  } = useAdminActivityState();
  
  // Track if initial refresh was done
  const initialRefreshDone = useRef(false);
  
  // Use optimized hooks with reduced Supabase interaction
  const {
    adminState,
    loadState,
    removeActiveAdmin,
    usingSupabase: stateUsingSupabase
  } = useAdminStateSync(0); // Set to 0 to disable polling
  
  const {
    adminActions,
    loadAdminActions,
    recordAdminAction,
    isThrottled,
    usingSupabase: actionsUsingSupabase
  } = useAdminActions();
  
  // Use the refresh hook
  const { refreshAdminData } = useAdminActivityRefresh(
    loadState,
    loadAdminActions,
    setIsLoading
  );
  
  // Use the action recording hook
  const { recordAction } = useAdminActionRecording(
    recordAdminAction,
    adminState
  );
  
  // Use the active user hook
  const { removeActiveAdmin: handleRemoveActiveAdmin } = useAdminActiveUser(
    removeActiveAdmin,
    removeAdminService
  );
  
  // Update throttled state when useAdminActions reports throttling
  useEffect(() => {
    setIsActionThrottled(isThrottled);
  }, [isThrottled, setIsActionThrottled]);
  
  // Set up minimal polling instead of real-time subscriptions - completely disabled
  useAdminRealtime(
    stateUsingSupabase && actionsUsingSupabase,
    refreshAdminData
  );
  
  // Add error recovery mechanism
  const recoverFromError = useCallback(async () => {
    try {
      console.log('Attempting to recover from error - NO PAGE REFRESH...');
      await refreshAdminData();
      toast({
        title: "Đã khôi phục kết nối",
        description: "Hệ thống đã tự động khôi phục sau lỗi."
      });
      return true;
    } catch (error) {
      console.error('Failed to recover from error:', error);
      return false;
    }
  }, [refreshAdminData, toast]);
  
  // Initial data load - only once
  useEffect(() => {
    if (!initialRefreshDone.current) {
      console.log('Performing initial admin data load - NO PAGE REFRESH');
      initialRefreshDone.current = true;
      refreshAdminData().catch(error => {
        console.error('Error during initial load:', error);
        // Schedule recovery attempt
        setTimeout(recoverFromError, 5000);
      });
    }
  }, [refreshAdminData, recoverFromError]);
  
  return {
    adminActions,
    adminState,
    isLoading,
    recordAction,
    removeActiveAdmin: handleRemoveActiveAdmin,
    refreshAdminData,
    isActionThrottled,
    usingSupabase: stateUsingSupabase && actionsUsingSupabase,
    recoverFromError
  };
};
